import { ElementType, forwardRef, HTMLAttributes, useMemo } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import parse, { domToReact } from 'html-react-parser'

import { removeParagraph } from '@/utils/strings'

import { cn } from '@/lib/utils'

const typographyTrims = cva('', {
  variants: {
    variant: {
      h1: '-mt-[0.27em] -mb-[0.43em] md:-mt-[0.17em] md:-mb-[0.33em]',
      h2: '-mt-[0.17em] -mb-[0.33em] md:-mt-[0.07em] md:-mb-[0.23em]',
      h3: '-mt-[0.17em] -mb-[0.33em] md:-mt-[0.17em] md:-mb-[0.33em]',
      h4: '-mt-[0.17em] -mb-[0.33em] md:-mt-[0.17em] md:-mb-[0.33em]',
      h5: '-mt-[0.16em] -mb-[0.34em] md:-mt-[0.16em] md:-mb-[0.34em]',
      h6: '-mt-[0.24em] -mb-[0.44em] md:-mt-[0.16em] md:-mb-[0.32em]',
      large: '-mt-[0.32em] -mb-[0.35em]',
      body: '-mt-[0.34em] -mb-[0.42em]',
      small: '-mt-[0.3em] -mb-[0.42em]',
      caption: '-mt-[0.38em] -mb-[0.52em]',
      overline: '-mt-[0.36em] -mb-[0.38em]'
    }
  }
})

const typographyVariants = cva('', {
  variants: {
    variant: {
      h1: 'uppercase font-monument text-s-h1 md:text-d-h1',
      h2: 'uppercase font-monument text-s-h2 md:text-d-h2',
      h3: 'uppercase font-monument text-s-h3 md:text-d-h3',
      h4: 'uppercase font-monument text-s-h4 md:text-d-h4',
      h5: 'uppercase font-monument text-s-h5 md:text-d-h5',
      h6: 'uppercase font-monument text-s-h6 md:text-d-h6',
      large: 'font-roboto text-large',
      body: 'font-roboto text-regular',
      small: 'font-roboto text-small',
      caption: 'font-roboto text-caption',
      overline: 'font-roboto text-overline'
    }
  },
  defaultVariants: {
    variant: 'body'
  }
})

const childrenClasses = cn([
  '[&_ul]:list-disc [&_ul]:pl-[1.45rem] [&_ul_br]:hidden [&_ul]:mt-[1.4rem]',
  '[&_u]:relative [&_u]:text-text-accent-light dark:[&_u]:text-text-accent-dark [&_u]:no-underline',
  '[&_u]:after:absolute [&_u]:after:bottom-[0.12em] [&_u]:after:left-0 [&_u]:after:w-full [&_u]:after:h-[.2rem]',
  '[&_u]:after:bg-text-accent-light dark:[&_u]:after:bg-text-accent-dark',
  '[&_u]:after:transition-transform [&_u]:after:duration-500 [&_u]:after:ease-in-out-cubic [&_u]:after:origin-left',
  '[&.reveal_u]:after:duration-0 [&.reveal_u]:after:scale-x-0',
  '[&_em]:not-italic [&_em]:text-text-accent-light dark:[&_em]:text-text-accent-dark',
  '[&_a]:text-text-accent-light dark:[&_a]:text-text-heading-invert-light'
])

export type TypographyProps = HTMLAttributes<HTMLElement> &
  VariantProps<typeof typographyVariants> &
  Partial<{
    component: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'
    paragraph: boolean
    gradient: boolean
    asChild: boolean
    mask: boolean
  }>

const options = {
  replace: (domNode: any) => {
    if (domNode.type === 'tag' && ['i', 'b', 'strong'].includes(domNode.name)) {
      const Tag = domNode.name
      return (
        <>
          {domNode.children.map((child: any, index: number) => {
            if (child.type === 'text') {
              return child.data.split(' ').map((word: string, wordIndex: number) => (
                <Tag key={`${index}-${wordIndex}`}>
                  {word}
                  {wordIndex < child.data.split(' ').length - 1 ? ' ' : ''}
                </Tag>
              ))
            }
            return domToReact(child, options)
          })}
        </>
      )
    }
  }
}

const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant,
      className,
      children,
      component = false,
      paragraph = false,
      asChild = false,
      mask = false,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : ((component || (paragraph ? 'p' : 'span')) as ElementType)

    const parsedChildren = useMemo(
      () => parse(children ? removeParagraph(children as string) : '', options),
      [children]
    )

    return mask ? (
      <Component
        ref={ref}
        className={cn(
          'overflow-clip',
          typographyVariants({ variant }),
          typographyTrims({ variant, className }),
          childrenClasses
        )}
        data-typography
        {...props}
      >
        <span
          data-label={parsedChildren}
          className={cn(
            'relative inline-flex',
            'after:absolute after:left-0 after:top-0 after:translate-y-full after:content-[attr(data-label)]',
            'after:transition-transform after:duration-500 after:ease-out-cubic',
            'group-hover:after:-translate-y-0'
          )}
        >
          <span
            className={cn(
              'transition-transform duration-500 ease-out-cubic',
              'group-hover:-translate-y-full'
            )}
          >
            {parsedChildren}
          </span>
        </span>
      </Component>
    ) : (
      <Component
        ref={ref}
        className={cn(
          typographyVariants({ variant }),
          typographyTrims({ variant, className }),
          childrenClasses,
          ''
        )}
        data-typography
        {...props}
      >
        {parsedChildren}
      </Component>
    )
  }
)

export default Typography
